import { create } from 'zustand';
import type { Node, Connection, AppState, ViewState } from './types';
import { timeCache } from './utils/timeCalculation';
import type { Node as ReactFlowNode, Edge } from '@xyflow/react';
import { base44 } from './api/base44Client';

// Update options for optimistic rendering
export interface UpdateOptions {
  skipHistory?: boolean;
  skipRender?: boolean;
}

// React Flow adapter functions
export function toReactFlowNodes(nodes: Record<string, Node>): ReactFlowNode[] {
  return Object.values(nodes).map((node) => ({
    id: node.id,
    type: node.type,
    position: { x: node.x, y: node.y },
    data: {
      text: node.text,
      timeEstimate: node.timeEstimate,
      parentId: node.parentId,
      children: node.children,
      // Style properties
      fillColor: node.fillColor,
      strokeColor: node.strokeColor,
      textColor: node.textColor,
      fontFamily: node.fontFamily,
      fontSize: node.fontSize,
      opacity: node.opacity,
      textOverflow: node.textOverflow,
    },
    selected: false,
  }));
}

export function toReactFlowEdges(connections: Connection[]): Edge[] {
  return connections.map((conn) => ({
    id: conn.id,
    source: conn.fromId,
    target: conn.toId,
    type: 'smoothstep',
    animated: false,
    style: {
      stroke: conn.strokeColor || 'var(--color-connection-default)',
      strokeWidth: 2,
      opacity: conn.opacity ?? 1,
    },
  }));
}

// ============ Simple Base44 Sync ============
// Maps local IDs to Base44 internal IDs
const nodeIdMap = new Map<string, string>();
const connectionIdMap = new Map<string, string>();
let lastSyncedState: string = '';

async function loadFromBase44(): Promise<{ nodes: Record<string, Node>; connections: Connection[] }> {
  try {
    const [b44Nodes, b44Connections] = await Promise.all([
      base44.entities.Node.list(),
      base44.entities.Connection.list(),
    ]);

    nodeIdMap.clear();
    connectionIdMap.clear();

    const nodes: Record<string, Node> = {};
    for (const n of b44Nodes) {
      if (!n.localId) continue; // Skip records without localId
      nodeIdMap.set(n.localId, n.id);
      nodes[n.localId] = {
        id: n.localId,
        x: n.x,
        y: n.y,
        text: n.text,
        timeEstimate: n.timeEstimate,
        type: n.type,
        parentId: n.parentId || null,
        children: n.children || [],
        width: n.width,
        height: n.height,
        // Style properties
        fillColor: n.fillColor,
        strokeColor: n.strokeColor,
        textColor: n.textColor,
        fontFamily: n.fontFamily,
        fontSize: n.fontSize,
        opacity: n.opacity,
        textOverflow: n.textOverflow,
      };
    }

    const connections: Connection[] = [];
    for (const c of b44Connections) {
      if (!c.localId) continue;
      connectionIdMap.set(c.localId, c.id);
      connections.push({
        id: c.localId,
        fromId: c.fromId,
        toId: c.toId,
        strokeColor: c.strokeColor,
        opacity: c.opacity,
      });
    }

    console.log(`[Base44] Loaded ${Object.keys(nodes).length} nodes, ${connections.length} connections`);
    return { nodes, connections };
  } catch (error) {
    console.error('[Base44] Failed to load:', error);
    throw error;
  }
}

async function saveToBase44(nodes: Record<string, Node>, connections: Connection[]): Promise<void> {
  // Create a hash of current state to avoid unnecessary syncs
  const currentState = JSON.stringify({ nodes, connections });
  if (currentState === lastSyncedState) {
    return; // No changes since last sync
  }

  const nodeIds = Object.keys(nodes);
  const connIds = connections.map(c => c.id);

  // Sync nodes
  for (const localId of nodeIds) {
    const node = nodes[localId];
    const data = {
      localId: node.id,
      x: node.x,
      y: node.y,
      text: node.text,
      timeEstimate: node.timeEstimate,
      type: node.type,
      parentId: node.parentId || undefined,
      children: node.children || [],
      width: node.width,
      height: node.height,
      // Style properties
      fillColor: node.fillColor,
      strokeColor: node.strokeColor,
      textColor: node.textColor,
      fontFamily: node.fontFamily,
      fontSize: node.fontSize,
      opacity: node.opacity,
      textOverflow: node.textOverflow,
    };

    try {
      const base44Id = nodeIdMap.get(localId);
      if (base44Id) {
        // Update existing
        await base44.entities.Node.update(base44Id, data);
      } else {
        // Create new
        const created = await base44.entities.Node.create(data);
        if (created?.id) {
          nodeIdMap.set(localId, created.id);
        }
      }
    } catch (error: any) {
      // If update failed with 404, create instead
      if (error?.status === 404) {
        nodeIdMap.delete(localId);
        try {
          const created = await base44.entities.Node.create(data);
          if (created?.id) {
            nodeIdMap.set(localId, created.id);
          }
        } catch (e) {
          console.warn(`[Base44] Failed to create node ${localId}:`, e);
        }
      } else {
        console.warn(`[Base44] Failed to sync node ${localId}:`, error);
      }
    }
  }

  // Sync connections
  for (const conn of connections) {
    const data = {
      localId: conn.id,
      fromId: conn.fromId,
      toId: conn.toId,
      strokeColor: conn.strokeColor,
      opacity: conn.opacity,
    };

    try {
      const base44Id = connectionIdMap.get(conn.id);
      if (base44Id) {
        await base44.entities.Connection.update(base44Id, data);
      } else {
        const created = await base44.entities.Connection.create(data);
        if (created?.id) {
          connectionIdMap.set(conn.id, created.id);
        }
      }
    } catch (error: any) {
      if (error?.status === 404) {
        connectionIdMap.delete(conn.id);
        try {
          const created = await base44.entities.Connection.create(data);
          if (created?.id) {
            connectionIdMap.set(conn.id, created.id);
          }
        } catch (e) {
          console.warn(`[Base44] Failed to create connection ${conn.id}:`, e);
        }
      } else {
        console.warn(`[Base44] Failed to sync connection ${conn.id}:`, error);
      }
    }
  }

  lastSyncedState = currentState;
  console.log(`[Base44] Synced ${nodeIds.length} nodes, ${connIds.length} connections`);
}

async function deleteFromBase44(nodeIds: string[], connectionIds: string[]): Promise<void> {
  for (const localId of nodeIds) {
    const base44Id = nodeIdMap.get(localId);
    if (base44Id) {
      try {
        await base44.entities.Node.delete(base44Id);
        nodeIdMap.delete(localId);
      } catch (e) {
        console.warn(`[Base44] Failed to delete node ${localId}:`, e);
      }
    }
  }

  for (const localId of connectionIds) {
    const base44Id = connectionIdMap.get(localId);
    if (base44Id) {
      try {
        await base44.entities.Connection.delete(base44Id);
        connectionIdMap.delete(localId);
      } catch (e) {
        console.warn(`[Base44] Failed to delete connection ${localId}:`, e);
      }
    }
  }
}

// ============ Store Definition ============

interface StoreState extends AppState {
  focusNodeId: string | null;
  searchResults: string[];
  currentSearchIndex: number;
  selectedEdgeId: string | null;

  addNode: (x: number, y: number, type?: 'task' | 'note' | 'milestone' | 'text' | 'rectangle' | 'circle' | 'diamond', parentId?: string) => void;
  addChild: (parentId: string, x: number, y: number, type?: 'task' | 'note' | 'milestone' | 'text' | 'rectangle' | 'circle' | 'diamond') => string;
  addSibling: (siblingId: string, x: number, y: number, type?: 'task' | 'note' | 'milestone' | 'text' | 'rectangle' | 'circle' | 'diamond') => string;
  addConnectedNode: (fromNodeId: string, direction: 'left' | 'right' | 'top' | 'bottom', type?: 'task' | 'note' | 'milestone' | 'text' | 'rectangle' | 'circle' | 'diamond') => string;
  updateNode: (id: string, updates: Partial<Node>, options?: UpdateOptions) => void;
  deleteNode: (id: string) => void;
  selectNode: (id: string | null) => void;
  selectEdge: (id: string | null) => void;
  addConnection: (fromId: string, toId: string) => void;
  updateConnection: (id: string, updates: Partial<Connection>, options?: UpdateOptions) => void;
  deleteConnection: (id: string) => void;
  updateViewState: (updates: Partial<ViewState>) => void;
  undo: () => void;
  redo: () => void;
  saveToLocalStorage: () => Promise<void>;
  loadFromLocalStorage: () => Promise<void>;
  exportToJSON: () => string;
  exportToPNG: () => void;
  searchNodes: (query: string) => void;
  nextSearchResult: () => void;
  previousSearchResult: () => void;
  clearSearch: () => void;
  clearCanvas: () => Promise<void>;
  clearTimeCache: () => void;
  panToElement: (id: string) => void;
  clearFocusNode: () => void;
}

const STORAGE_KEY = 'excaliplan-state';

const addToHistory = (state: StoreState) => {
  const snapshot = {
    nodes: { ...state.nodes },
    connections: [...state.connections],
  };

  state.history.past.push(snapshot);
  if (state.history.past.length > 100) {
    state.history.past.shift();
  }
  state.history.future = [];
};

export const useStore = create<StoreState>((set, get) => ({
  nodes: {},
  connections: [],
  selectedNodeId: null,
  selectedEdgeId: null,
  focusNodeId: null,
  searchResults: [],
  currentSearchIndex: -1,
  viewState: {
    offsetX: 0,
    offsetY: 0,
    zoom: 1,
  },
  history: {
    past: [],
    future: [],
  },

  addNode: (x, y, type = 'task', parentId) => {
    set((state) => {
      addToHistory(state);

      const id = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newNode: Node = {
        id,
        x,
        y,
        text: '',
        timeEstimate: '',
        type,
        parentId: parentId || null,
        children: [],
      };

      const newNodes = { ...state.nodes, [id]: newNode };

      if (parentId && newNodes[parentId]) {
        newNodes[parentId] = {
          ...newNodes[parentId],
          children: [...newNodes[parentId].children, id],
        };
      }

      const newConnections = [...state.connections];
      if (parentId && newNodes[parentId]) {
        const connId = `conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        newConnections.push({ id: connId, fromId: parentId, toId: id });
      }

      return {
        nodes: newNodes,
        connections: newConnections,
        selectedNodeId: id,
      };
    });
  },

  addChild: (parentId, x, y, type = 'task') => {
    let newNodeId = '';
    set((state) => {
      addToHistory(state);

      const parent = state.nodes[parentId];
      if (!parent) return state;

      const id = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      newNodeId = id;

      const newNode: Node = {
        id,
        x,
        y,
        text: '',
        timeEstimate: '',
        type,
        parentId,
        children: [],
      };

      const newNodes = {
        ...state.nodes,
        [id]: newNode,
        [parentId]: {
          ...parent,
          children: [...parent.children, id],
        },
      };

      const connId = `conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      return {
        nodes: newNodes,
        connections: [...state.connections, { id: connId, fromId: parentId, toId: id }],
        selectedNodeId: id,
      };
    });
    return newNodeId;
  },

  addSibling: (siblingId, x, y, type = 'task') => {
    let newNodeId = '';
    set((state) => {
      addToHistory(state);

      const sibling = state.nodes[siblingId];
      if (!sibling) return state;

      const id = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      newNodeId = id;

      const newNode: Node = {
        id,
        x,
        y,
        text: '',
        timeEstimate: '',
        type,
        parentId: sibling.parentId,
        children: [],
      };

      const newNodes = { ...state.nodes, [id]: newNode };

      if (sibling.parentId && newNodes[sibling.parentId]) {
        newNodes[sibling.parentId] = {
          ...newNodes[sibling.parentId],
          children: [...newNodes[sibling.parentId].children, id],
        };
      }

      const newConnections = [...state.connections];
      if (sibling.parentId && newNodes[sibling.parentId]) {
        const connId = `conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        newConnections.push({ id: connId, fromId: sibling.parentId, toId: id });
      }

      return {
        nodes: newNodes,
        connections: newConnections,
        selectedNodeId: id,
      };
    });
    return newNodeId;
  },

  addConnectedNode: (fromNodeId, direction, type = 'task') => {
    let newNodeId = '';
    set((state) => {
      addToHistory(state);

      const fromNode = state.nodes[fromNodeId];
      if (!fromNode) return state;

      const id = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      newNodeId = id;

      // Calculate position based on direction
      const spacing = 200;
      let x = fromNode.x;
      let y = fromNode.y;

      switch (direction) {
        case 'left':
          x = fromNode.x - spacing;
          break;
        case 'right':
          x = fromNode.x + spacing;
          break;
        case 'top':
          y = fromNode.y - spacing;
          break;
        case 'bottom':
          y = fromNode.y + spacing;
          break;
      }

      const newNode: Node = {
        id,
        x,
        y,
        text: '',
        timeEstimate: '',
        type,
        parentId: fromNodeId,
        children: [],
      };

      const newNodes = {
        ...state.nodes,
        [id]: newNode,
        [fromNodeId]: {
          ...fromNode,
          children: [...fromNode.children, id],
        },
      };

      // Create connection from fromNode to new node
      const connId = `conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newConnections = [
        ...state.connections,
        { id: connId, fromId: fromNodeId, toId: id }
      ];

      return {
        nodes: newNodes,
        connections: newConnections,
        selectedNodeId: id,
      };
    });
    return newNodeId;
  },

  updateNode: (id, updates, options = {}) => {
    set((state) => {
      if (!state.nodes[id]) return state;

      if (!options.skipHistory) {
        addToHistory(state);
      }

      if (updates.timeEstimate !== undefined) {
        timeCache.invalidate(id, state.nodes, state.connections);
      }

      return {
        nodes: {
          ...state.nodes,
          [id]: { ...state.nodes[id], ...updates },
        },
      };
    });
  },

  deleteNode: (id) => {
    const state = get();
    if (!state.nodes[id]) return;

    const node = state.nodes[id];
    let shouldDeleteChildren = true;

    if (node.children.length > 0) {
      shouldDeleteChildren = window.confirm(
        `This node has ${node.children.length} child node(s). Delete them too?\n\nOK = Delete children\nCancel = Re-parent to grandparent`
      );
    }

    const deletedNodeIds: string[] = [];
    const deletedConnectionIds: string[] = [];

    set((state) => {
      addToHistory(state);

      const newNodes = { ...state.nodes };

      if (node.parentId && newNodes[node.parentId]) {
        newNodes[node.parentId] = {
          ...newNodes[node.parentId],
          children: newNodes[node.parentId].children.filter((cid) => cid !== id),
        };
      }

      if (shouldDeleteChildren) {
        const deleteRecursive = (nodeId: string) => {
          const n = newNodes[nodeId];
          if (!n) return;
          n.children.forEach(deleteRecursive);
          deletedNodeIds.push(nodeId);
          delete newNodes[nodeId];
        };
        deleteRecursive(id);
      } else {
        for (const childId of node.children) {
          if (newNodes[childId]) {
            newNodes[childId] = { ...newNodes[childId], parentId: node.parentId };
            if (node.parentId && newNodes[node.parentId]) {
              newNodes[node.parentId] = {
                ...newNodes[node.parentId],
                children: [...newNodes[node.parentId].children, childId],
              };
            }
          }
        }
        deletedNodeIds.push(id);
        delete newNodes[id];
      }

      let newConnections = state.connections.filter((conn) => {
        const shouldRemove = shouldDeleteChildren
          ? !newNodes[conn.fromId] || !newNodes[conn.toId]
          : conn.fromId === id || conn.toId === id;
        if (shouldRemove) {
          deletedConnectionIds.push(conn.id);
          return false;
        }
        return true;
      });

      if (!shouldDeleteChildren && node.parentId) {
        for (const childId of node.children) {
          if (newNodes[childId] && newNodes[node.parentId]) {
            const connId = `conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            newConnections.push({ id: connId, fromId: node.parentId, toId: childId });
          }
        }
      }

      return {
        nodes: newNodes,
        connections: newConnections,
        selectedNodeId: state.selectedNodeId === id ? null : state.selectedNodeId,
      };
    });

    // Delete from Base44 in background
    deleteFromBase44(deletedNodeIds, deletedConnectionIds);
  },

  selectNode: (id) => set({ selectedNodeId: id, selectedEdgeId: null }),

  selectEdge: (id) => set({ selectedEdgeId: id, selectedNodeId: null }),

  addConnection: (fromId, toId) => {
    set((state) => {
      if (state.connections.some((c) => c.fromId === fromId && c.toId === toId)) return state;
      if (fromId === toId) return state;

      addToHistory(state);

      const id = `conn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newNodes = { ...state.nodes };

      if (newNodes[fromId] && newNodes[toId]) {
        newNodes[toId] = { ...newNodes[toId], parentId: fromId };
        newNodes[fromId] = { ...newNodes[fromId], children: [...newNodes[fromId].children, toId] };
      }

      timeCache.invalidate(toId, newNodes, [...state.connections, { id, fromId, toId }]);

      return {
        connections: [...state.connections, { id, fromId, toId }],
        nodes: newNodes,
      };
    });
  },

  updateConnection: (id, updates, options = {}) => {
    set((state) => {
      const connIndex = state.connections.findIndex((c) => c.id === id);
      if (connIndex === -1) return state;

      if (!options.skipHistory) {
        addToHistory(state);
      }

      const newConnections = [...state.connections];
      newConnections[connIndex] = { ...newConnections[connIndex], ...updates };

      return {
        connections: newConnections,
      };
    });
  },

  deleteConnection: (id) => {
    const state = get();
    const conn = state.connections.find((c) => c.id === id);
    if (!conn) return;

    set((state) => {
      addToHistory(state);

      const newNodes = { ...state.nodes };
      if (newNodes[conn.fromId] && newNodes[conn.toId]) {
        newNodes[conn.toId] = { ...newNodes[conn.toId], parentId: null };
        newNodes[conn.fromId] = {
          ...newNodes[conn.fromId],
          children: newNodes[conn.fromId].children.filter((cid) => cid !== conn.toId),
        };
      }

      const newConnections = state.connections.filter((c) => c.id !== id);
      timeCache.invalidate(conn.toId, newNodes, newConnections);

      return { connections: newConnections, nodes: newNodes };
    });

    deleteFromBase44([], [id]);
  },

  updateViewState: (updates) => {
    set((state) => ({ viewState: { ...state.viewState, ...updates } }));
  },

  undo: () => {
    set((state) => {
      if (state.history.past.length === 0) return state;

      const previous = state.history.past[state.history.past.length - 1];
      timeCache.clear();

      return {
        nodes: previous.nodes,
        connections: previous.connections,
        history: {
          past: state.history.past.slice(0, -1),
          future: [{ nodes: state.nodes, connections: state.connections }, ...state.history.future],
        },
      };
    });
  },

  redo: () => {
    set((state) => {
      if (state.history.future.length === 0) return state;

      const next = state.history.future[0];
      timeCache.clear();

      return {
        nodes: next.nodes,
        connections: next.connections,
        history: {
          past: [...state.history.past, { nodes: state.nodes, connections: state.connections }],
          future: state.history.future.slice(1),
        },
      };
    });
  },

  saveToLocalStorage: async () => {
    const state = get();

    // Save to localStorage immediately
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      nodes: state.nodes,
      connections: state.connections,
    }));

    // Sync to Base44 in background
    try {
      await saveToBase44(state.nodes, state.connections);
    } catch (error) {
      console.error('[Store] Base44 sync failed:', error);
    }
  },

  loadFromLocalStorage: async () => {
    try {
      // Try Base44 first
      const { nodes, connections } = await loadFromBase44();
      if (Object.keys(nodes).length > 0 || connections.length > 0) {
        set({ nodes, connections });
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ nodes, connections }));
        return;
      }
    } catch (error) {
      console.error('[Store] Base44 load failed, using localStorage:', error);
    }

    // Fallback to localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        set({ nodes: data.nodes || {}, connections: data.connections || [] });
      } catch (e) {
        console.error('[Store] localStorage parse failed:', e);
      }
    }
  },

  exportToJSON: () => {
    const state = get();
    return JSON.stringify({ nodes: state.nodes, connections: state.connections }, null, 2);
  },

  searchNodes: (query) => {
    const state = get();
    if (!query?.trim()) {
      set({ searchResults: [], currentSearchIndex: -1 });
      return;
    }

    const lowerQuery = query.toLowerCase().trim();
    const matches = Object.values(state.nodes)
      .filter((n) => n.text.toLowerCase().includes(lowerQuery))
      .sort((a, b) => a.text.length - b.text.length);

    if (matches.length > 0) {
      set({
        searchResults: matches.map((n) => n.id),
        currentSearchIndex: 0,
        selectedNodeId: matches[0].id,
        focusNodeId: matches[0].id,
      });
    } else {
      alert('No nodes found matching: ' + query);
      set({ searchResults: [], currentSearchIndex: -1 });
    }
  },

  nextSearchResult: () => {
    const state = get();
    if (state.searchResults.length === 0) return;
    const nextIndex = (state.currentSearchIndex + 1) % state.searchResults.length;
    set({
      currentSearchIndex: nextIndex,
      selectedNodeId: state.searchResults[nextIndex],
      focusNodeId: state.searchResults[nextIndex],
    });
  },

  previousSearchResult: () => {
    const state = get();
    if (state.searchResults.length === 0) return;
    const prevIndex = state.currentSearchIndex === 0
      ? state.searchResults.length - 1
      : state.currentSearchIndex - 1;
    set({
      currentSearchIndex: prevIndex,
      selectedNodeId: state.searchResults[prevIndex],
      focusNodeId: state.searchResults[prevIndex],
    });
  },

  clearSearch: () => set({ searchResults: [], currentSearchIndex: -1 }),

  exportToPNG: () => {
    alert('PNG export: Take a screenshot for now. Full export coming soon!');
  },

  clearCanvas: async () => {
    const state = get();
    const nodeIds = Object.keys(state.nodes);
    const connectionIds = state.connections.map((c) => c.id);

    set((state) => {
      addToHistory(state);
      timeCache.clear();
      return { nodes: {}, connections: [], selectedNodeId: null };
    });

    deleteFromBase44(nodeIds, connectionIds);
  },

  clearTimeCache: () => timeCache.clear(),

  panToElement: (id) => {
    const state = get();
    if (!state.nodes[id]) return;
    set({ selectedNodeId: id, focusNodeId: id });
  },

  clearFocusNode: () => set({ focusNodeId: null }),
}));
